import { useState } from "react";
import { toast } from "sonner";
import { api, MEDIA, RESERVATION_SLOTS } from "../lib/api";

export default function Reserve() {
  const today = new Date().toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "19:30",
    guests: 2,
    occasion: "",
    special_requests: "",
  });
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/reservations", {
        ...form,
        guests: Number(form.guests),
      });
      setConfirmed(data);
      toast.success("Reservation received");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Something went wrong. Please try again.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length > 0)
        msg = detail.map((d) => (typeof d === "string" ? d : d?.msg)).filter(Boolean).join(" · ");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div data-testid="reserve-confirmation" className="pt-40 pb-24 max-w-[760px] mx-auto px-6">
        <div className="border border-[#2E3628] p-12 md:p-16 text-center bg-[#1C2219]/40">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4C1A0] mb-8">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#D4C1A0]" fill="none" stroke="currentColor" strokeWidth="1.4">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="overline mb-4">Reservation request received</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F9F6F0] leading-tight">
            Thank you, {confirmed.name}.
          </h1>
          <p className="mt-6 text-[#9AA894] leading-relaxed max-w-lg mx-auto">
            We have received your request for {confirmed.guests} on{" "}
            <span className="text-[#F9F6F0]">{confirmed.date}</span> at{" "}
            <span className="text-[#F9F6F0]">{confirmed.time}</span>. A note has been sent to{" "}
            <span className="text-[#D4C1A0]">{confirmed.email}</span>. Our host will reach out shortly to confirm.
          </p>

          <div className="mt-10 inline-flex flex-col items-center border border-[#D4C1A0] px-8 py-5 bg-[#0F120E]">
            <div className="overline text-[10px] text-[#D4C1A0]">15% off applied</div>
            <p className="mt-2 text-sm text-[#F9F6F0] max-w-sm">
              Your reservation is eligible for our permanent 15% discount on dining when made through chica.com.
            </p>
          </div>

          <p className="mt-10 text-[10px] text-[#9AA894] tracking-[0.3em] uppercase">
            Reference · {confirmed.id.slice(0, 8).toUpperCase()}
          </p>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a
              href={`https://wa.me/919520928111?text=Hi%2C%20my%20reservation%20reference%20is%20${confirmed.id.slice(0, 8).toUpperCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-whatsapp"
              className="btn-line"
            >
              WhatsApp us
            </a>
            <button
              data-testid="confirmation-new-btn"
              onClick={() => setConfirmed(null)}
              className="btn-line"
            >
              Make another reservation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reserve-page" className="pt-32 pb-24">
      <section className="relative h-[40vh] min-h-[300px] flex items-end overflow-hidden">
        <img src={MEDIA.interior} alt="Reserve" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F120E]/40 to-[#0F120E]" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 pb-12 z-10 w-full">
          <div className="overline mb-4">Reserve a table</div>
          <h1 className="font-display text-5xl sm:text-7xl text-[#F9F6F0]">Hold an evening.</h1>
        </div>
      </section>

      <section className="max-w-[760px] mx-auto px-6 md:px-12 py-16">
        <div
          data-testid="reserve-offer-banner"
          className="border border-[#D4C1A0] bg-[#1C2219]/60 px-6 py-5 mb-12 flex items-start gap-4"
        >
          <div className="font-display text-3xl text-[#D4C1A0] leading-none mt-1">15%</div>
          <div>
            <div className="overline text-[10px] text-[#D4C1A0]">Permanent dining offer</div>
            <p className="mt-1 text-sm text-[#F9F6F0] leading-relaxed">
              All reservations made through chica.com receive 15% off the final dining bill — applied automatically when you arrive.
            </p>
          </div>
        </div>

        <p className="text-[#9AA894] leading-relaxed mb-12">
          Tables are released two months in advance. We hold a small number each evening for spontaneous
          guests — please share your details and we will return shortly to confirm.
        </p>

        <form onSubmit={submit} className="space-y-10" data-testid="reserve-form">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-10">
            <div>
              <label className="overline">Full name</label>
              <input
                data-testid="reserve-name-input"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your name"
                className="input-line mt-2"
              />
            </div>
            <div>
              <label className="overline">Email</label>
              <input
                type="email"
                data-testid="reserve-email-input"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@email.com"
                className="input-line mt-2"
              />
            </div>
            <div>
              <label className="overline">Phone</label>
              <input
                data-testid="reserve-phone-input"
                required
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 ..."
                className="input-line mt-2"
              />
            </div>
            <div>
              <label className="overline">Guests</label>
              <select
                data-testid="reserve-guests-input"
                value={form.guests}
                onChange={(e) => update("guests", e.target.value)}
                className="input-line mt-2 appearance-none bg-[#0F120E]"
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} {i === 0 ? "guest" : "guests"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline">Date</label>
              <input
                type="date"
                data-testid="reserve-date-input"
                required
                min={today}
                max={maxDate}
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                className="input-line mt-2"
              />
            </div>
            <div>
              <label className="overline">Time</label>
              <select
                data-testid="reserve-time-input"
                value={form.time}
                onChange={(e) => update("time", e.target.value)}
                className="input-line mt-2 appearance-none bg-[#0F120E]"
              >
                {RESERVATION_SLOTS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline">Occasion (optional)</label>
              <input
                data-testid="reserve-occasion-input"
                value={form.occasion}
                onChange={(e) => update("occasion", e.target.value)}
                placeholder="Anniversary, birthday, business…"
                className="input-line mt-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline">Special requests (optional)</label>
              <textarea
                data-testid="reserve-notes-input"
                value={form.special_requests}
                onChange={(e) => update("special_requests", e.target.value)}
                placeholder="Allergies, seating preferences…"
                className="input-line mt-2"
                rows={3}
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="reserve-submit-btn"
            disabled={loading}
            className="btn-line"
          >
            {loading ? "Sending…" : "Request reservation · 15% off"}
          </button>
        </form>
      </section>
    </div>
  );
}
